import React, { useCallback, useEffect, useContext, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Handlebars from 'handlebars';
import pdfMake from 'pdfmake/build/pdfmake';
import vfsFonts from 'pdfmake/build/vfs_fonts';

import FirebaseContext from "../../firebase/context";
import { content, footer } from './template.js';
import './Invoice.css';

const paymentMethodName = field => {
    switch (field) {
        case 'CASH':
            return 'gotówka';
        case 'PAYPALME':
            return 'e-płatność';
        case 'STRIPE':
            return 'e-płatność';
        default:
            return field;
    }
};

const z = (num, size) => {
    const s = `000000000${num}`;
    return s.substr(s.length - size);
};

const wordNumbers = number => String(number).split('').map(digit => {
    switch (digit) {
        case '1':
            return 'jed';
        case '2':
            return 'dwa';
        case '3':
            return 'trz';
        case '4':
            return 'czt';
        case '5':
            return 'pię';
        case '6':
            return 'sze';
        case '7':
            return 'sie';
        case '8':
            return 'osi';
        case '9':
            return 'dzi';
        default:
            return 'zer';
    }
}).join('-');

const { vfs } = vfsFonts.pdfMake;
pdfMake.vfs = vfs;

export default () => {
    const { invoiceId, downloadKey } = useParams();
    const firebase = useContext(FirebaseContext);

    const [invoice, setInvoice] = useState(null);
    const [formattedDate, setFormattedDate] = useState('0000-00-00');

    useEffect(() => {
        (firebase && invoiceId && downloadKey) && (async () => {
            const callParams = { invoiceId, downloadKey };
            const { invoiceFields, error } = await firebase.callFunction('getInvoice', callParams);
            if (error) {
                console.log(`Could not get invoice: ${error}`);
                alert('Błąd, odnośnik do faktury jest uszkodzony lub faktura nie istnieje')
            } else {
                const d = new Date(invoiceFields.timestamp);
                setFormattedDate(`${d.getFullYear()}-${z(d.getMonth() + 1, 2)}-${z(d.getDate(), 2)}`);
                setInvoice(invoiceFields);
            }
        })();
    }, [firebase, invoiceId, downloadKey]);

    const getPdfContent = useMemo(() => {
        if (invoice && formattedDate) {
            const { buyer, seller, vat, net, gross, paymentMethod } = invoice;
            const pdfContext = {
                ...invoice,
                formattedDate,
                sellerNipLine: seller.nip ? `NIP: ${seller.nip}` : `PESEL: ${seller.pesel}`,
                buyerNipLine: buyer.nip ? `NIP: ${buyer.nip}` : `PESEL: ${buyer.pesel}`,
                netPrice: vat ? net.toFixed(2) : gross.toFixed(2),
                vatPrice: vat ? (gross - net).toFixed(2) : '0.00',
                grossPrice: gross.toFixed(2),
                spokenPrice: wordNumbers(Math.floor(gross)),
                groszPrice: Math.floor((gross - Math.floor(gross)) * 100),
                paymentMethod: paymentMethodName(paymentMethod),
            };

            const pdfObject = { content, footer };
            const pdfTemplate = Handlebars.compile(JSON.stringify(pdfObject));

            return JSON.parse(pdfTemplate(pdfContext));
        }
        return {};
    }, [invoice, formattedDate]);

    const download = useCallback(() => {
        pdfMake.createPdf(getPdfContent).download(`faktura_${invoiceId}.pdf`);
    }, [invoiceId, getPdfContent]);

    const open = useCallback(() => {
        pdfMake.createPdf(getPdfContent).open();
    }, [getPdfContent]);

    const print = useCallback(() => {
        pdfMake.createPdf(getPdfContent).print();
    }, [getPdfContent]);

    if (!invoice) {
        return <div>Ładowanie...</div>
    }

    const { buyer, item, seller, title, vat, net, gross, paymentMethod } = invoice;

    return (
        <div id="pdf-wrapper">
            <div className="Invoice-download-wrapper">
                <span className="Invoice-download" onClick={download} style={{ fontWeight: '600' }}>
                Pobierz plik PDF
            </span>
                <span className="Invoice-download" onClick={open}>
                Otwórz
            </span>
                <span className="Invoice-download" onClick={print}>
                Drukuj
            </span>
            </div>
            <div id="pdf">
                <p className="c32 title">
                    <span className="c56">Faktura</span>
                </p>
                <p className="c32 title">
                    <span className="c26">
                        {`Nr. ${title}`}
                    </span>
                </p>
                <table className="c21">
                    <tbody>
                    <tr className="c33">
                        <td className="c7" colSpan="1" rowSpan="1">
                            <p className="c14">
                                <span className="c0">&nbsp;</span>
                            </p>
                        </td>
                        <td className="c7" colSpan="1" rowSpan="1">
                            <p className="c14">
                                <span className="c0">&nbsp;</span>
                            </p>
                        </td>
                        <td className="c58" colSpan="1" rowSpan="1">
                            <p className="c14">
                                <span className="c0">&nbsp;</span>
                            </p>
                        </td>
                        <td className="c57" colSpan="1" rowSpan="1">
                            <p className="c52">
                                <span className="c0">
                                    Data wystawienia:
                                    <br />
                                    Data sprzedaży:
                                </span>
                            </p>
                        </td>
                        <td className="c35" colSpan="1" rowSpan="1">
                            <p className="c52">
                                <span className="c41">
                                    {formattedDate}
                                    <br />
                                </span>
                                <span className="c41">
                                    {formattedDate}
                                </span>
                                <span className="c36">
                                    <br />
                                </span>
                            </p>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <table className="c21">
                    <tbody>
                    <tr className="c10">
                        <td className="c28" colSpan="1" rowSpan="1">
                            <p className="c20">
                                <span className="c0">Sprzedawca:</span>
                            </p>
                            <p className="c20">
                                <span className="c36">
                                    {seller.name}
                                </span>
                            </p>
                            <p className="c20">
                                <span className="c0">
                                    {seller.street}
                                </span>
                            </p>
                            <p className="c20">
                                <span className="c46">
                                    {`${seller.postalCode} ${seller.locality}`}
                                </span>
                            </p>
                            <p className="c20">
                                <span className="c0">
                                    {`${seller.nip ? 'NIP' : 'PESEL'}: ${seller.nip || seller.pesel}`}
                                </span>
                            </p>
                        </td>
                        <td className="c49" colSpan="1" rowSpan="1">
                            <p className="c20">
                                <span className="c46">
                                    Nabywca:
                                    <br />
                                </span>
                                <span className="c41">
                                    {buyer.name}
                                    <br />
                                </span>
                                <span className="c46">
                                    {buyer.street}
                                </span>
                            </p>
                            <p className="c16">
                                <span className="c0">
                                    {`${buyer.postalCode} ${buyer.locality}`}
                                </span>
                            </p>
                            <p className="c20">
                                <span className="c0">
                                    {`${buyer.nip ? 'NIP' : 'PESEL'}: ${buyer.nip || buyer.pesel}`}
                                </span>
                            </p>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <table className="c21">
                    <tbody>
                    <tr className="c10">
                        <td className="c34" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">Lp</span>
                            </p>
                        </td>
                        <td className="c19" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">Nazwa towaru lub usługi</span>
                            </p>
                        </td>
                        <td className="c43" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">Jm</span>
                            </p>
                        </td>
                        <td className="c6" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">Ilość</span>
                            </p>
                        </td>
                        <td className="c39" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">Cena netto</span>
                            </p>
                        </td>
                        <td className="c17" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">Stawka VAT</span>
                            </p>
                        </td>
                        <td className="c15" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">Wartość netto</span>
                            </p>
                        </td>
                        <td className="c40" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">Wartość VAT</span>
                            </p>
                        </td>
                        <td className="c1" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">Wartość&nbsp;brutto</span>
                            </p>
                        </td>
                    </tr>
                    <tr className="c10">
                        <td className="c34" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">1</span>
                            </p>
                        </td>
                        <td className="c19" colSpan="1" rowSpan="1">
                            <p className="c45">
                                <span className="c0">
                                    {item}
                                </span>
                            </p>
                        </td>
                        <td className="c43" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">Szt.</span>
                            </p>
                        </td>
                        <td className="c6" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">1</span>
                            </p>
                        </td>
                        <td className="c39" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">
                                    {vat ? net.toFixed(2) : gross.toFixed(2)}
                                </span>
                            </p>
                        </td>
                        <td className="c17" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">
                                    {`${vat}%`}
                                </span>
                            </p>
                        </td>
                        <td className="c15" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">
                                    {vat ? net.toFixed(2) : gross.toFixed(2)}
                                </span>
                            </p>
                        </td>
                        <td className="c40" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">
                                    {vat ? (gross - net).toFixed(2) : '0.00'}
                                </span>
                            </p>
                        </td>
                        <td className="c1" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c0">
                                    {gross.toFixed(2)}
                                </span>
                            </p>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <table className="c21">
                    <tbody>
                    <tr className="c55">
                        <td className="c44" colSpan="1" rowSpan="1">
                            <p className="c23">
                                <span className="c0">
                                    Forma zapłaty:
                                    <br />
                                    Termin zapłaty:
                                    <br />
                                </span>
                            </p>
                        </td>
                        <td className="c11" colSpan="1" rowSpan="1">
                            <p className="c23">
                                <span className="c36">
                                    {paymentMethodName(paymentMethod)}
                                    <br />
                                    {formattedDate}
                                    <br />
                                    <br />
                                </span>
                            </p>
                        </td>
                        <td className="c29" colSpan="1" rowSpan="1">
                            <p className="c23">
                                <span className="c0">
                                  Razem:
                                  <br />
                                  Słownie:
                                </span>
                            </p>
                        </td>
                        <td className="c38" colSpan="1" rowSpan="1">
                            <p className="c23">
                                <span className="c41">
                                    {`${gross.toFixed(2)} PLN`}
                                    <br />
                                </span>
                                <span className="c46">
                                    {`${wordNumbers(Math.floor(gross))} `}
                                </span>
                                <span className="c0">
                                    {`PLN ${Math.floor((gross - Math.floor(gross)) * 100)}/100`}
                                </span>
                            </p>
                        </td>
                    </tr>
                    </tbody>
                </table>
                {/* <table className="c21">
                    <tbody>
                    <tr className="c10">
                        <td className="c9" colSpan="1" rowSpan="1">
                            <p className="c22 c24">
                                <span className="c0">&nbsp;</span>
                            </p>
                        </td>
                        <td className="c54" colSpan="1" rowSpan="1">
                            <p className="c22">
                                <span className="c0">Przemysław K&#281;dzierski</span>
                            </p>
                        </td>
                        <td className="c9" colSpan="1" rowSpan="1">
                            <p className="c22 c24">
                                <span className="c0">&nbsp;</span>
                            </p>
                        </td>
                        <td className="c51" colSpan="1" rowSpan="1">
                            <p className="c22">
                                <span className="c0">Agnieszka Klientka-Intercars</span>
                            </p>
                        </td>
                    </tr>
                    <tr className="c10">
                        <td className="c9" colSpan="1" rowSpan="1">
                            <p className="c2 c24">
                                <span className="c0">&nbsp;</span>
                            </p>
                        </td>
                        <td className="c18" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c8">
                                  Osoba upoważniona do wystawienia
                                </span>
                            </p>
                        </td>
                        <td className="c9" colSpan="1" rowSpan="1">
                            <p className="c2 c24">
                                <span className="c8">&nbsp;</span>
                            </p>
                        </td>
                        <td className="c59" colSpan="1" rowSpan="1">
                            <p className="c2">
                                <span className="c8">Osoba upoważniona do odbioru</span>
                            </p>
                        </td>
                    </tr>
                    </tbody>
                </table> */}
                <table className="c42 footer">
                    <tbody>
                    <tr className="c10">
                        <td className="c31" colSpan="1" rowSpan="1">
                            <p className="c30">
                                <span className="c13">Faktura wygenerowana przez</span>
                            </p>
                        </td>
                        <td className="c48" colSpan="1" rowSpan="1">
                            <p className="c22 c50">
                            <span
                                style={{
                                    overflow: 'hidden',
                                    display: 'inline-block',
                                    margin: '0.00px 0.00px',
                                    border: '0.00px solid #000000',
                                    transform: 'rotate(0.00rad) translateZ(0px)',
                                    width: '134.00px',
                                    height: '20.00px',
                                }}
                            >
                              <img
                                  alt=""
                                  src="data:image/png;base64, iVBORw0KGgoAAAANSUhEUgAAANUAAAAgCAYAAACFHz0pAAAAAXNSR0IArs4c6QAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAA1aADAAQAAAABAAAAIAAAAACl2KneAAAPz0lEQVR4Ae2c3W4cSRWAuzohGy5QhhsEQpDOBcLRCnnMXiEk0g4PkMkTZPIEcZ7A4yfI5AkyeYJ4r5EyYwnEBbCeIIUYceGxkGCFhHZywcb7M118p7vLXd1T1dMe25usMiW1q+r81alT59Rf91gFq3RqC2zGt7WPaTh6rny4twWP49s9lNp2tU9Hdkaj5z0XbgVbzgKXm7DFcdwOgvAOtK0g0JRNUhNKE6X0aDgc7hnoKl9Z4H22gDeoCCQCKHxAEG2pQFE2yTERaxUwewfMeoMgSJj5RhNDfZE5OkboeM/TxhEz8MCDC2T2BofK8wm+nXnoCrKyQDMLOIOqvF1wBJFHNpRdnLwbx5sj/PUuwTX1kJ4XOKLNnluY3gM+cOOCAL5tHw74Tg1uhXpPLKBfftjWs9kjuhspFdxXvzgYNel6aBMx87cIiOECh7NZnGVWthi3PURex0mwAq4s8A5bQO+3W7O//vxRMpvts5WJeaJEB0OBCW6R6idBJQFFIBBQEhBnT9mWMVyowNlbWklYWeD8LDB7cXNrFr451FptaaKp/Chwx+NFrVnbPzUgENqLGJri0edx3ZnGlkNAs7yq6zZMa/3iG9g+2k2ee1kmKvq1Tl+kf1MamC57oWNk2Uoil3Pj+Z5faSdGV4ZPHEq/Rv5CJ7J1sssunQW/rA1s2edd/mp/LQ6VfkSfiQH2ao5EoD2+HHzQc6BKoDSo6HyHgJLbvbr0Wgd6F1PzBFOeFuenmOY7lMsBEeiPR6PhFnBvos0I/m3kIUNF1SsDYAFb0TH4PgP71CvIg8Arrm1ubt6y0U2d0MF3KucSZ8r71g20aklfTP/o05RBo0+LL0NyOXJZJOPTNjJMn3IbTZA34ILo8bKTkNVOdimF8STl8kXfQX4BRbk+ZbKCe/B0XToLd36ptZvrPKpK3EzP5Ko0djaNDpJN+nrCl7WpDmmvZdOZMn4r/tgxdTt/s78WXSaYgHUSGSdXUsHe10nQ/e7Gq4mNlkDUoV6/sv73xzY8lUInhfi6jSiX9R6KdXyDRqdiFYSDTIZ+AW3soxW55YsQgdQn5I1xrPvIJC9S3u6wgNSXtPVOpu5d07wUvTccDWMDr+NlwG/gULKNjgy9K6dPdGe46cIJTPqGnGc+R6nyIW+a22i3iquzNzbBIfStNACqjFY9ly/OXBoDi0R07qDzk6Y6C28+tneROzGykNPGn/ZNvZpXbUf/+jgyk48zsRgkEfKxTzl9sb/WU4GGzx2MUB8ht/udjfIFhQTipeyiq5tJ1GP6/NDQhXQgBlEXUC/EoVxKZQKDANwIxdsyQHS4S3WuA4aWmXqAotum3iTPBlwNxdhN6N8uTRoI0SId6BPdud130QHv4lQSmC0X3gUTWnieCa8L74OJM8K70K65/P3cX+bE0e6WtH8anUVI1rYSuSc64D9jfGlnrpEcAI+oEUuVrC19yFFzGXK2qv745f6H7S8+WTskorfZ0rXK5yYCXQevk0Q9vLJxEJlAEcFySUEgboda70PTLfhUWy4yvtzPLjK4qAg7c5pYAIKkFm9IRXG2NNKBsYFVcwzPzKDuVeFN6tlgKZxGtlbvbsqcpJl+4gz0J7Kpcyd5YsNOU0bmE2TEp+E5Ha0S+aUxyCaB4NHp5BTU+djKpBkZKL7Uw41fmPp8rrYFBm9/Hmcges91rp8lsw7BFvEQV+WH6tMr6mp09aNXJblf7N/sfKGPJZh6mgmsyif1hMuN4+R4K2T5a1P3JFGqWJY9RI3AYjAGfNtPLNvGYEceaI5cdBgwYhLYcuG+vbDypEYfBzV9YSsjuwGxUfoezkOaOZwHeSZwdQyyAEvPJGeV2yJEerYQJnTvWKMHTd9+Bs8tm8cqYyvdterlIkasRNReqC5tfPDLg67aGE8Nsaxqx5+sDXWin0EfVXiqMlI2VirVNgKqORE5qsKWr5cNVpaTntlimZ3kka0keGdg0QvvUl+RSZAmm/bDXDIo07hrNk9W9g+uW0IKRf+6mdZw6tiU8JIO47Fu6pUcJ0na2IfdwPOebMlxmo8rNGk1c7hiO+WiqcLwsYfI38ie4GEVX67re0U97NJeq6hXS+lkeT+zYyr3dZXC1JFzTyZfU2dCH6HXY1Ov5iq7JKuC0zp8+JJnQUgYGQjMwwXFw6sfHcRXNl6OjbDP2Oq9+fPak5m8q0p4V2XR15UDZMvt3zUjaNmcGaOPig5nUE9xgIHIlYGW3JVwDjHA1OCkLDd/8Fw3MJPLAGJ4nMu/zcxpp9CMDN9p8mX58jbE+WNkpAOErjFnjV1wTjvjGK1CN9UpyuUSYzpA5qQMVSL3ThlmaukKODa1uhzZDxmnvkUzZkwZM/eWjjGI6FYk+rDT6croupO54BpOc/wIvhH22HfTCzTV29Il6eWwOV/wy5DXMUNLRpkSvy9prBK5CCvS53+5uRXM3mzLNq+ANithyzSomlHXUGVbyPllmAb2hE0GgMxnFJm5JtDcw3htgjNm0MjrktAF4zqKt4Wjz6wkRcBTHuGgA9zuwSKdoIn8NMmIq/47HKyNjWI/rThNsQLW0YE7qgRUSi4wbjm3qPjGTcZgQkvrKYPjD5Nln+5PbZTYhgnzKWPMeM8n9O4A7RuM8OMbXQJxaGCL8tptX8ocskoxUnmaBUX5f3+6ucvKxETFJm6phKY48Z6ft+685edyYCIHzICuocYhjzgeN0iLAiplq5Nn5L6V3KzM5caTUbnuq81PTIYS+7CnV7vYqIeNYgM/e64nfhl1uLCNs7f8vOmt8K4bf7pjBYHFNtC91a3KJzx2JHCrcLuesFRxW3fylHA6aNm4U5eRHSJ7agutlG8tMlyF/oKqso1ID+iyN7+B4/YuqKGLEltn4/Nq8wjHe4qd0rOR/V6trgHoJz58HS7niXy89fBkUo93YVVtoFgcC20t2z/7XBR8XXDb8GXKIpszlX9fzozI1aF6BN39otkLL4lzYECZzZKxzFIX3uK704Bsha81UAc6zbuc1EYj6MVOC53JJZeVL3LBBVaHEzxtjtkiSvGUKd2+N+ZhYpd3UdvNGPQ29LvoNvHS4/kagSbNTIFcgkIxmyyd4CWokl0WrD5CnINJ213OBBNWh52lG1pw/smWdj1AvjjHxG4HA7WkDnwpp7FlvfvldDK55dZTbtHknUw60YyrNNgpqtquSuOuK9mNzPEKDPfy6CKSEqODdyLAb7ru7XDN93WOlZPJfSAtNknZQhA8gXbTRy+BYx2jSmScp3yoEl1dJWQgpgQXg+VPBFaPw+VQDsoYuyWUkss3chjuCfzrfm4TELXXy9fhR5W5gIoY2KE8tEe5eUInkblUoq14KcYzMhE0xlHnJGX9mQ8oGQcZA2zEVwm3u3OMjQDlF7oiE3nIrE2prkyIIz9VumpENl50xPHv2bByWVbfIkG/hS61/lVQZyXkyxB2qnBTlzNVeWunHnz2x7VI8GX46esiW67USUmf1Qrl3auVUIiihHBMnn4QKTAJaQKOlP1Ni54/OMwAqkcuNDLbiJKg7YGfCg23XF3ou1KWBB6n4UvLBYfQjDrVN2JAnlEf87R4XlfOYkfArvM4kjjZ7UGGkO8Y/d/oOZjPAErH4YFLADZiKx4wufCdG/++QGjyL6q53AmifAxE78C9OgiHO8nYZva9Pcoo8o+c3eQC5cbQTICyw3B/jI3cKJcLTTqukT2mwKqJVU92TllivCN6ue33L5mofQGXThSo6d7hSPBYqYO/xf/9w1ofm+IraHmGlN4bSsPybgU5spRfUEpfvB75hEtgye0Wz0ieqvHFqeisrFixJWNsleeKyOjw9Hi2GJw7NkF2brMhRVmcIeeDN531ogJ7cSVxVPR66mtBbCB6FTYK+6KrTQ9eAgtHPF3K+9yFn0dFddz4Y8/g0XkX2+6ZejXPdZbAx5bFJFmlkzpy+3YQwDsQfhct1GyH09cGRy58xhc+ceGqt3/5DR+3fqrHs37qGz8UP+FhpUqDShqmM2NwON/FBJYYi8DtuDrZFJYZSnUNfSaz2VUrvJUr4PSCxohakIfxAoJzRMvXG7Vb5YVt8a7nLnOPxxkXsi8gkBer5f/9gXOfg9+k3+n1TONMDMj0n+los5uNfxHghtfkBHEHO3RM3eT4/Zm3eb5tosg+CSppUIyVr1jO6BeahomvCuT7NNnOFAkjELjJXSBLrYjIfMxWrFtIlJLu8aepvBjaNGWO0dR5G79INeKXzjNHka8UmupWbSqbwUVOFeOpN7WdsDOu8x9Y5+Mag1/Sb7IvL6QBSQRCRP+304rjT+YH2buoPMBr2i2fF0WcCtUuQfHaFxhng+tpKaikwdxA7SwoGjursEriOlw+ipXfr8h3fPMDC2wXfBuj7WUsTf6Ko8gP055vVamRJ4HaBd7AOVTH5s+2D42ct7R1tGVcRDnrk47FeZr1K9VCJrL7vJ9qu+zu0xOePs9DH76Ai+Onv0uaFLCilI8DfuPfvhbUJ6V08q3+tIhdRc22TwJXPl0qUj7+BcAqubaBP/j1y/Hnsw+imQ4en2zbMMKZyvg+V/N3f/ibV2zJa1K+hYiJ7Q6EBMLcoZAgSq+Bx6gk7wbIm6dsRgrZw8tKwJmquCghQNL3MCJ30ERuLkv27h3kXLe0yHXUImfXgp8U2WqIDl10uHUCTIM01QGeVIepwWW/TDW1cu566YpubQa3X6bManLj55osDG02BmEXe8TIaAO3+pa+FJ+AQ8eA7s1PYsCDon9SKye5QJLZXnSk/9gvnXiu5VRiuxHyoWn+vhBZEZugjkdnJki5PFJILG8jpU14pZ89KbsS+si5S/pbStxhDRj7qAS0KvB14ZtYoLT47yG/3lVy+z3n21VSX50VT/V/vPm3niFAj1VaWWBlgX8N+YiWyxQC3kwoC41C8PANo+79aPNgYhOvgsq2xqr8XlvgcMj/q2DVwQiLtvt7fHbR+8nmwchlsFVQuazigOlXa5G6WcxIUg++Yit2afY6mF26dpIbXgP/3vELdWMyFbA+jFrB8dWWLSeFv/xZW334j7FhFTrDY2Cr/JuzwD9/txYHoRqwal2vtCrHkq2f/vZgUIGXqu9tUOn9qPVmdvUZ1ogsi9hlC3wxRV40poHEvwRrSwvs+6ecJ1pWecrAjhikT8F+yq3UJAzV9+G7BnHELdaUfHL10vHHaiML3IvR9P2Tejjkn2Z+/eUWL9q30/HQaie4fKV/Y7P4VbDPKu9tUIlB/vP7tTFOvO4zzrcPzqWCCkaXw+Dp9391MPn26f/uaSxbQtHqxmZze77XQSXGkqX+8uVgIofNdNkX4DmlJFTtsPKrUoGx6rQw/EQlwaTalFZJS6swXbmqOG6oWry1bBE4kzAJRkKrdDgVOh0GUaCTqdRNf+b5V5BvwgL/B7wR87XDkENSAAAAAElFTkSuQmCC"
                                  style={{
                                      width: '134.00px',
                                      height: '20.00px',
                                      marginLeft: '0.00px',
                                      marginTop: '0.00px',
                                      transform: 'rotate(0.00rad) translateZ(0px)',
                                  }}
                                  title=""
                              />
                            </span>
                            </p>
                        </td>
                        <td className="c31" colSpan="1" rowSpan="1">
                            <p className="c20">
                                <span className="c53">
                                  <a
                                      className="c37"
                                      href="https://www.getthebox.eu"
                                  >
                                    www.getthebox.eu
                                  </a>
                                </span>
                            </p>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

